import dayjs from 'dayjs';
import { computed, toValue, type MaybeRefOrGetter, type ComputedRef } from 'vue';

interface UseIsEventNotEndedParams {
  endAt?: MaybeRefOrGetter<string | null | undefined>;
  startAt?: MaybeRefOrGetter<string | null | undefined>;
  allDay?: MaybeRefOrGetter<boolean | undefined>;
}

/**
 * Composable that determines if an event has not yet ended
 * Returns reactive computed ref that updates when parameters change
 * @param params - Event timing parameters
 * @param params.endAt - End timestamp (ISO 8601) - can be a value, ref, or getter function
 * @param params.startAt - Start timestamp for fallback - can be a value, ref, or getter function
 * @param params.allDay - Whether it's an all-day event - can be a value, ref, or getter function
 * @returns Computed ref that is true if event hasn't ended, false otherwise
 */
export const useIsEventNotEnded = ({ 
  endAt, 
  startAt, 
  allDay = false 
}: UseIsEventNotEndedParams): ComputedRef<boolean> => {
  return computed(() => {
    // Use endAt, fallback to startAt for backwards compatibility
    const endTime = toValue(endAt) || toValue(startAt);
    if (!endTime) return false;

    const now = dayjs();
    const endDate = dayjs(endTime);
    const isAllDay = toValue(allDay) ?? false;

    // All-day events should show the button if they're happening today or in the future
    if (isAllDay) {
      return endDate.isSame(now, 'day') || endDate.isAfter(now, 'day');
    }

    return endDate.isAfter(now);
  });
};
